<template>
  <div>
    <div class="attachments-view">
      <div v-for="item in attachmentsList" :key="item.id" class="attachment mt-base mb-sm">
        <div class="attachment-box relative">
          <vimeo-player v-if="item.type === 'embed/vimeo'" :value="item" />
          <div v-else-if="item.type === 'embed/mixcloud'" v-html="item.html"/>
          <video
            v-else-if="isVideo(item.type)"
            :src="item.url"
            :width="(item.dimensions || {}).w"
            :height="(item.dimensions || {}).h"
            controls
            preload="metadata"
            class="bg-gray-400"
            @error="handleVideoError($event, item)">
            Your browser does not support the video tag.
            <a v-if="item.url" :href="item.url" target="_blank">Download the video</a> instead.
          </video>
          <div
            v-else-if="item.type === 'application/pdf'"
            class="pdf-thumbnail-wrapper relative bg-gray-400"
            :class="{'outline': true, 'cursor-pointer': true}"
            @click.stop.prevent="handlePdfClick($event, item)"
            @mousedown.stop.prevent="$event.preventDefault()"
            @touchstart.stop.prevent="handlePdfClick($event, item)"
            @touchend.stop.prevent="$event.preventDefault()"
            @contextmenu.prevent="$event.preventDefault()">
            <img
              v-if="item.url && (!item.url.toLowerCase().endsWith('.pdf') || item.url.startsWith('data:image'))"
              :src="item.url"
              :alt="item.caption || 'PDF preview'"
              :width="(item.dimensions || {}).w"
              :height="(item.dimensions || {}).h"
              class="bg-gray-400"
              @error="handleImageError($event, item)"
              @load="handleImageLoad($event, item)"
              @mousedown.prevent
              @dragstart.prevent
              draggable="false"/>
            <div v-else class="pdf-placeholder text-center p-base">
              <i class="material-icons" style="font-size: 48px; color: #999;">description</i>
              <div class="text-sm text-gray-600 mt-sm">PDF Document</div>
            </div>
          </div>
          <img
            v-else
            :src="item.url"
            :alt="item.caption"
            :width="(item.dimensions || {}).w"
            :height="(item.dimensions || {}).h"
            class="bg-gray-400"
            @error="handleImageError($event, item)"
            @load="handleImageLoad($event, item)"/>
          <button
            v-if="item.type === 'application/pdf'"
            class="button compact absolute top-0 right-0 mt-sm mr-sm flex items-center justify-center bg-milk z-10 px-base py-sm pdf-view-button"
            @click.stop.prevent="handlePdfButtonClick($event, item)"
            @mousedown.stop.prevent="handlePdfButtonClick($event, item)"
            @contextmenu.prevent.stop="handlePdfButtonClick($event, item)"
            @auxclick.prevent.stop="handlePdfButtonClick($event, item)"
            @touchstart.stop.prevent="handlePdfButtonClick($event, item)"
            type="button"
            tabindex="0">
            <span>View</span>
          </button>
          <div v-if="item.caption" class="text-block text-sm">
            {{item.caption}}
          </div>
          <div v-if="item.videoError" class="text-block text-sm text-red-600 mt-sm">
            Video not available: {{item.videoError}}
          </div>
          <div v-if="item.imageError" class="text-block text-sm text-red-600 mt-sm">
            Image not available: {{item.imageError}}
            <a v-if="item.url" :href="item.url" target="_blank" class="text-blue-600 underline ml-sm">Open image in new tab</a>
          </div>
        </div>
      </div>
    </div>
    <popover-modal :open="pdfPreviewOpen" :hideHeader="true" @close="closePdfPreview" @esc="closePdfPreview" class="pdf-modal">
      <button
        class="pdf-close-button"
        @click="closePdfPreview"
        type="button">
        <i class="material-icons">close</i>
      </button>
      <div class="pdf-preview-container">
        <div v-if="pdfError" class="pdf-error text-red-600 text-center p-base">
          <p class="mb-base">{{ pdfError }}</p>
          <div class="mt-base">
            <button
              v-if="item && item.pdfUrl"
              @click.stop.prevent="openPdfInNewTab(item.pdfUrl)"
              class="button bg-blue-600 text-white px-base py-sm rounded">
              Open PDF in new tab
            </button>
            <a
              v-else-if="currentPdfUrl"
              :href="currentPdfUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="text-blue-600 underline"
              @click.stop>
              Open PDF in new tab
            </a>
          </div>
        </div>
        <div v-else-if="currentPdfUrl" class="pdf-wrapper" ref="pdfWrapper" :class="{ 'safari-pdf': isSafari, 'pdfjs-viewer': usePdfJs }">
          <div class="pdf-toolbar-overlay">
            <span class="reading-view-label">Reader view</span>
          </div>
          <div class="pdf-content-wrapper">
          <!-- PDF.js container (only when usePdfJs is true) -->
          <div v-if="usePdfJs" class="pdfjs-canvas-container" ref="pdfJsContainer">
            <div v-if="pdfJsLoading" class="pdf-loading">
              <div class="text-center">Loading PDF...</div>
              <div class="text-center text-sm text-gray-500 mt-sm">This may take a moment...</div>
              <div class="text-center mt-base">
                <button
                  @click.stop.prevent="openPdfInNewTab(currentPdfUrl)"
                  class="button bg-gray-500 text-white px-base py-sm rounded">
                  Open in new tab instead
                </button>
              </div>
            </div>
            <div v-else-if="pdfJsError" class="pdf-error text-red-600 text-center p-base">
              <p>{{ pdfJsError }}</p>
              <button
                @click.stop.prevent="openPdfInNewTab(currentPdfUrl)"
                class="button bg-blue-600 text-white px-base py-sm rounded mt-base">
                Open PDF in new tab
              </button>
            </div>
            <!-- Canvas should always be in DOM (for ref), just hidden when loading/error -->
            <canvas v-show="!pdfJsLoading && !pdfJsError" ref="pdfJsCanvas" class="pdfjs-canvas" style="display: block !important; visibility: visible !important; opacity: 1 !important;"></canvas>
            <!-- Debug info if PDF loaded but canvas not visible -->
            <div v-if="!pdfJsLoading && !pdfJsError && pdfJsDocument && !pdfJsCanvas" class="pdf-error text-yellow-600 text-center p-base">
              <p>PDF loaded but canvas not rendered.</p>
              <p class="text-xs mt-sm">Document: loaded ({{ pdfJsDocument.numPages }} pages), Canvas: {{ $refs.pdfJsCanvas ? 'found' : 'not found' }}</p>
              <button
                @click.stop.prevent="retryPdfLoad"
                class="button bg-blue-600 text-white px-base py-sm rounded mt-base">
                Retry
              </button>
            </div>
          </div>
          <!-- Mobile: use object/embed directly to prevent downloads -->
          <div v-else-if="isMobile && !usePdfJs && currentPdfUrl" class="pdf-iframe-container pdf-mobile-container">
            <object
              :data="getPdfUrlWithoutToolbar(currentPdfUrl)"
              type="application/pdf"
              class="pdf-embed pdf-embed-object pdf-mobile-embed"
              style="width: 100%; height: 100vh; min-height: 100vh; display: block; visibility: visible; opacity: 1;"
              @contextmenu.prevent
              @load="handlePdfLoad"
              @error="handlePdfObjectError">
              <embed
                :src="getPdfUrlWithoutToolbar(currentPdfUrl)"
                type="application/pdf"
                class="pdf-embed pdf-mobile-embed"
                style="width: 100%; height: 100vh; min-height: 100vh; display: block; visibility: visible; opacity: 1;"
                @contextmenu.prevent
                @load="handlePdfLoad"
                @error="handlePdfObjectError" />
            </object>
          </div>
          <!-- Desktop: use object/embed to prevent downloads (same as mobile) -->
          <div v-else-if="!isMobile && !usePdfJs && currentPdfUrl" class="pdf-iframe-container" style="width: 100%; height: auto; min-height: 100vh; position: relative;">
            <object
              :data="getPdfUrlWithoutToolbar(currentPdfUrl)"
              type="application/pdf"
              class="pdf-embed pdf-embed-object pdf-desktop-embed"
              style="width: 100%; height: auto; min-height: 100vh; border: none; background: white; pointer-events: auto; display: block;"
              @contextmenu.prevent>
              <embed
                :src="getPdfUrlWithoutToolbar(currentPdfUrl)"
                type="application/pdf"
                class="pdf-embed pdf-desktop-embed"
                style="width: 100%; height: auto; min-height: 100vh; border: none; background: white; display: block; pointer-events: auto;"
                @contextmenu.prevent />
            </object>
          </div>
          <!-- Legacy fallback (should not be needed) -->
          <template v-else-if="!isMobile">
            <!-- Desktop fallback (should rarely be used) -->
            <iframe
              v-if="isSafari"
              :src="getPdfUrlWithoutToolbar(currentPdfUrl)"
              type="application/pdf"
              class="pdf-embed pdf-embed-safari"
              style="background: white !important; border: none;"
              @contextmenu.prevent
              @load="handlePdfIframeLoad"
              @error="handlePdfIframeError">
            </iframe>
          <object
              v-else
            :data="getPdfUrlWithoutToolbar(currentPdfUrl)"
            type="application/pdf"
            class="pdf-embed"
              style="background: white !important;"
            @contextmenu.prevent>
            <embed
              :src="getPdfUrlWithoutToolbar(currentPdfUrl)"
              type="application/pdf"
              class="pdf-embed"
                style="background: white !important;"
              @contextmenu.prevent />
          </object>
          </template>
          </div>
        </div>
        <!-- Desktop scroll buttons - positioned relative to preview container -->
        <button
          v-if="currentPdfUrl && !pdfError"
          class="pdf-scroll-button pdf-scroll-up"
          @click="scrollPdfUp"
          type="button"
          title="Scroll up">
          <i class="material-icons">arrow_upward</i>
        </button>
        <button
          v-if="currentPdfUrl && !pdfError"
          class="pdf-scroll-button pdf-scroll-down"
          @click="scrollPdfDown"
          type="button"
          title="Scroll down">
          <i class="material-icons">arrow_downward</i>
        </button>
        <div v-else class="pdf-loading">
          <div class="text-center">Loading PDF...</div>
        </div>
      </div>
    </popover-modal>
  </div>
</template>

<script>
import VimeoPlayer from './VimeoPlayer'
import PopoverModal from './UI/PopoverModal'
import PDFJS from 'pdfjs-dist'
// eslint-disable-next-line import/no-webpack-loader-syntax
import PdfjsWorker from 'worker-loader!pdfjs-dist/es5/build/pdf.worker.js'

// Configure PDF.js worker - check if already configured
if (PDFJS && PDFJS.GlobalWorkerOptions && !PDFJS.GlobalWorkerOptions.workerPort) {
  try {
    PDFJS.GlobalWorkerOptions.workerPort = new PdfjsWorker()
    console.log('[AttachmentsView] PDF.js worker configured')
  } catch (error) {
    console.error('[AttachmentsView] Failed to configure PDF.js worker:', error)
    // Try alternative worker setup
    if (typeof window !== 'undefined' && window.PDFJS) {
      PDFJS.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS.version}/pdf.worker.min.js`
    }
  }
} else {
  console.log('[AttachmentsView] PDF.js worker already configured')
}

export default {
  name: 'AttachmentsView',
  components: { VimeoPlayer, PopoverModal },
  props: {
    attachments: { type: Object, required: true }
  },

  data: () => ({
    videoErrors: {},
    imageErrors: {},
    pdfPreviewOpen: false,
    currentPdfUrl: null,
    pdfError: null,
    observer: null,
    isSafari: false,
    isMobile: false,
    pdfJsTimeout: null
  }),

  computed: {
    attachmentsList () {
      const count = Object.keys(this.attachments || {}).length
      if (!count) return []
      return Object.entries(this.attachments)
        .sort((a, b) => a.order - b.order)
        .reduce((res, [id, item]) => {
          const { preview, full, original } = item.srcSet
          let src
          if (item.type === 'embed/vimeo') {
            src = full
            res.push({ id, type: item.type, vimeoId: item.name, url: src.url, dimensions: src.dimensions, caption: item.caption })
          } else if (item.type === 'embed/mixcloud') {
            src = full
            res.push({ id, type: item.type, html: item.html, url: src.url, dimensions: src.dimensions, caption: item.caption })
          } else {
            // For PDFs, prefer preview image over actual PDF file to prevent download
            if (item.type === 'application/pdf') {
              // Store the actual PDF URL first (for the viewer)
              let pdfUrl = null
              if (original && original.url) {
                pdfUrl = original.url
              } else if (full && full.url) {
                pdfUrl = full.url
              }

              // For display, ONLY use preview image - never use the PDF file itself
              // If no preview exists, we'll use a placeholder or hide the image
              let displayUrl = null
              if (preview && preview.url) {
                displayUrl = preview.url
              } else if (full && full.url && !full.url.toLowerCase().endsWith('.pdf')) {
                // Only use full if it's NOT a PDF file
                displayUrl = full.url
              }

              // If we have a PDF URL but no preview image, create a placeholder
              // NEVER use the PDF URL as the image source - it will trigger downloads
              if (pdfUrl && !displayUrl) {
                // Use a data URI placeholder or a generic PDF icon
                displayUrl = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5QREY8L3RleHQ+PC9zdmc+'
              }

              // NEVER use PDF URL as image source - always use placeholder if no preview
              const imageUrl = displayUrl || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5QREY8L3RleHQ+PC9zdmc+'

              const attachmentItem = {
                id,
                type: item.type,
                url: imageUrl, // NEVER use PDF URL - always use preview or placeholder
                dimensions: (preview || full || original || {}).dimensions,
                caption: item.caption,
                original,
                pdfUrl: pdfUrl // Always store the actual PDF URL separately
              }
              res.push(attachmentItem)
            } else {
              src = full || (preview || {}).size > original.size ? preview : original
              const attachmentItem = { id, type: item.type, url: src.url, dimensions: src.dimensions, caption: item.caption, original }
              if (this.videoErrors[id]) {
                attachmentItem.videoError = this.videoErrors[id]
              }
              if (this.imageErrors[id]) {
                attachmentItem.imageError = this.imageErrors[id]
              }
              res.push(attachmentItem)
            }
          }
          return res
        }, [])
    }
  },

  watch: {
    pdfPreviewOpen (newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.hideHeaderCloseButton()
          // Also hide it after multiple delays to ensure DOM is ready
          setTimeout(() => {
            this.hideHeaderCloseButton()
          }, 50)
          setTimeout(() => {
            this.hideHeaderCloseButton()
          }, 100)
          setTimeout(() => {
            this.hideHeaderCloseButton()
          }, 200)
        })
      } else {
        // Show blog close button when PDF closes
        this.$nextTick(() => {
          // Update the injected style tag to remove blog-close-button hiding rules
          const styleTag = document.getElementById('pdf-modal-hide-header')
          if (styleTag) {
            // Remove blog-close-button rules from the style tag
            styleTag.textContent = styleTag.textContent.replace(/\.pdf-modal\.popover-modal\s*~\s*\.blog-close-button[^}]*\}/g, '')
            styleTag.textContent = styleTag.textContent.replace(/body:has\(\.pdf-modal\.popover-modal\)\s*\.blog-close-button[^}]*\}/g, '')
          }
          // Directly restore the blog close button with !important to override any remaining styles
          const blogCloseButton = document.querySelector('.blog-close-button')
          if (blogCloseButton) {
            blogCloseButton.style.setProperty('display', 'flex', 'important')
            blogCloseButton.style.setProperty('visibility', 'visible', 'important')
            blogCloseButton.style.setProperty('opacity', '1', 'important')
            blogCloseButton.style.setProperty('pointer-events', 'auto', 'important')
            blogCloseButton.style.setProperty('height', 'auto', 'important')
            blogCloseButton.style.setProperty('width', 'auto', 'important')
            blogCloseButton.style.setProperty('position', 'absolute', 'important')
            blogCloseButton.style.setProperty('top', '0.5rem', 'important')
            blogCloseButton.style.setProperty('right', '0.5rem', 'important')
          }
        })
      }
    }
  },

  mounted () {
    // Detect Safari browser
    this.isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    // Detect mobile devices
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                    (window.innerWidth && window.innerWidth <= 768)
    console.log('[AttachmentsView] isSafari detected:', this.isSafari, 'isMobile:', this.isMobile)
    // Hide header button if PDF is already open
    if (this.pdfPreviewOpen) {
      this.hideHeaderCloseButton()
    }
    // Set up MutationObserver to catch any dynamically added header buttons
    this.observer = new MutationObserver(() => {
      if (this.pdfPreviewOpen) {
        this.hideHeaderCloseButton()
      }
    })
  },

  beforeDestroy () {
    if (this.observer) {
      this.observer.disconnect()
    }
  },

  methods: {
    isVideo (type) {
      return type && type.startsWith('video/')
    },
    handleVideoError (event, item) {
      const video = event.target
      let errorMessage = 'Video could not be loaded'
      if (video.error) {
        switch (video.error.code) {
          case video.error.MEDIA_ERR_ABORTED:
            errorMessage = 'Video loading was aborted'
            break
          case video.error.MEDIA_ERR_NETWORK:
            errorMessage = 'Network error while loading video'
            break
          case video.error.MEDIA_ERR_DECODE:
            errorMessage = 'Video decoding error'
            break
          case video.error.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorMessage = 'Video format not supported'
            break
        }
      }
      this.$set(this.videoErrors, item.id, errorMessage)
    },
    handleImageError (event, item) {
      console.error('[AttachmentsView] Image failed to load:', item.url, item)
      const img = event.target
      let errorMessage = 'Image could not be loaded'
      if (img && !img.complete) {
        errorMessage = 'Image failed to load - network error or invalid URL'
      } else if (img && img.naturalWidth === 0) {
        errorMessage = 'Image failed to load - invalid image format'
      }
      this.$set(this.imageErrors, item.id, errorMessage)
    },
    handleImageLoad (event, item) {
      console.log('[AttachmentsView] Image loaded successfully:', item.url, item)
      // Clear any previous error for this image
      if (this.imageErrors[item.id]) {
        this.$delete(this.imageErrors, item.id)
      }
    },
    handlePdfIframeLoad (event) {
      console.log('[AttachmentsView] PDF iframe loaded successfully')
      // Prevent any download triggers from iframe
      try {
        const iframe = event.target
        if (iframe && iframe.contentWindow) {
          // Try to prevent download in iframe (may be blocked by CORS)
          iframe.contentWindow.addEventListener('beforeunload', (e) => {
            e.preventDefault()
            return false
          }, { once: true })
        }
      } catch (e) {
        // CORS restriction - can't access iframe content
        console.log('[AttachmentsView] Cannot access iframe content (CORS):', e)
      }
    },
    handlePdfLoad (event) {
      console.log('[AttachmentsView] PDF object/embed loaded')
      // Clear any previous errors
      this.pdfError = null
      // On mobile, ensure scrolling works after PDF loads
      if (this.isMobile) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.ensureMobileScrolling()
          }, 100)
        })
      }
    },
    handlePdfObjectError (event) {
      console.error('[AttachmentsView] PDF object/embed error:', event)
      this.pdfError = 'Failed to load PDF. The connection may have failed or the PDF is not accessible. Please try opening it in a new tab.'
    },
    handlePdfIframeError (event) {
      console.error('[AttachmentsView] PDF iframe error:', event)
      // On mobile, if iframe fails (likely due to download), try object/embed instead
      if (this.isMobile && !this.usePdfJs) {
        console.log('[AttachmentsView] Iframe failed on mobile, will try object/embed fallback')
        // The template will show object/embed as fallback
      } else if (this.usePdfJs && this.currentPdfUrl && !this.pdfJsLoading && !this.pdfJsError) {
        console.log('[AttachmentsView] Iframe failed, trying PDF.js...')
        this.loadPdfWithPdfJs(this.currentPdfUrl)
      } else {
        // Don't show error that causes confusion - PDF.js should handle it
        console.warn('[AttachmentsView] Iframe failed, PDF.js already tried or not available')
      }
    },
    handlePdfClick (event, item) {
      // Prevent all default behaviors that might trigger download
      if (event) {
        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        // Prevent any link navigation
        if (event.target && event.target.closest('a')) {
          const link = event.target.closest('a')
          if (link) {
            link.preventDefault()
            link.href = 'javascript:void(0)'
          }
        }
      }
      console.log('[AttachmentsView] PDF clicked, preventing download, opening preview', item)
      // Small delay to ensure event propagation is stopped
      setTimeout(() => {
        this.openPdfPreview(item)
      }, 10)
      return false
    },
    handlePdfButtonClick (event, item) {
      // Prevent all default behaviors that might trigger download
      if (!event) {
        return false
      }

      // Immediately prevent all default behaviors
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()

      // Prevent any link navigation
      const link = event.target && event.target.closest('a')
      if (link) {
        link.preventDefault()
        link.href = 'javascript:void(0)'
        link.onclick = (e) => {
          e.preventDefault()
          e.stopPropagation()
          e.stopImmediatePropagation()
          return false
        }
      }

      // Prevent middle-click, right-click, etc.
      if (event.button !== undefined && event.button !== 0) {
        return false
      }

      // Prevent any form submission
      const form = event.target && event.target.closest('form')
      if (form) {
        form.onsubmit = (e) => {
          e.preventDefault()
          e.stopPropagation()
          return false
        }
      }

      // Prevent any navigation attempts
      if (event.target && event.target.href) {
        event.target.href = 'javascript:void(0)'
      }

      console.log('[AttachmentsView] PDF button clicked, preventing download, opening preview', item)

      // Open preview immediately (no delay needed if we've prevented everything)
      this.openPdfPreview(item)

      return false
    },
    openPdfInNewTab (pdfUrl) {
      // Open PDF in new tab for viewing (not downloading)
      if (pdfUrl) {
        // Use window.open to open in new tab - this should display the PDF, not download it
        const newWindow = window.open(pdfUrl, '_blank', 'noopener,noreferrer')
        if (!newWindow) {
          // If popup blocked, fall back to creating a link
          const link = document.createElement('a')
          link.href = pdfUrl
          link.target = '_blank'
          link.rel = 'noopener noreferrer'
          // DO NOT add download attribute - we want to view, not download
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      }
    },
    retryPdfLoad () {
      // Retry loading PDF
      if (this.currentPdfUrl) {
        console.log('[AttachmentsView] Retrying PDF load...')
        this.pdfJsLoading = true
        this.pdfJsError = null
        this.pdfJsCanvas = null
        this.pdfJsDocument = null
        this.pdfJsPage = null
        this.$nextTick(() => {
          this.loadPdfWithPdfJs(this.currentPdfUrl)
        })
      }
    },
    async loadPdfWithPdfJs (pdfUrl) {
      console.log('[AttachmentsView] loadPdfWithPdfJs called with:', pdfUrl)
      if (!pdfUrl) {
        console.warn('[AttachmentsView] No PDF URL provided')
        this.usePdfJs = false
        this.pdfJsError = 'No PDF URL available'
        this.pdfJsLoading = false
        return
      }

      if (typeof PDFJS === 'undefined') {
        console.error('[AttachmentsView] PDF.js not available, typeof PDFJS:', typeof PDFJS)
        this.usePdfJs = false
        this.pdfJsError = 'PDF.js library not loaded'
        this.pdfJsLoading = false
        return
      }

      console.log('[AttachmentsView] PDF.js is available, starting load...')
      console.log('[AttachmentsView] PDF.js worker status:', PDFJS.GlobalWorkerOptions.workerPort ? 'configured' : 'NOT configured')

      // Check if PDF.js worker is actually configured
      if (!PDFJS.GlobalWorkerOptions.workerPort) {
        console.error('[AttachmentsView] PDF.js worker not configured!')
        this.pdfJsError = 'PDF.js worker not initialized. Please refresh the page and try again.'
        this.pdfJsLoading = false
        this.usePdfJs = false
        return
      }

      this.pdfJsLoading = true
      this.pdfJsError = null
      this.usePdfJs = true // Ensure it's enabled

      // Add timeout to detect if PDF.js hangs (reduced to 3 seconds for faster feedback)
      // Clear any existing timeout first
      if (this.pdfJsTimeout) {
        clearTimeout(this.pdfJsTimeout)
      }
      this.pdfJsTimeout = setTimeout(() => {
        if (this.pdfJsLoading) {
          console.error('[AttachmentsView] PDF.js loading timeout after 3 seconds')
          // On mobile, fall back to object/embed instead of showing error
          if (this.isMobile) {
            console.log('[AttachmentsView] PDF.js timeout on mobile, falling back to object/embed')
            this.usePdfJs = false
            this.pdfJsLoading = false
            this.pdfJsError = null
          } else {
            this.pdfJsError = 'PDF loading is taking too long. The PDF may be blocked by CORS restrictions. Please try opening it in a new tab.'
            this.pdfJsLoading = false
            this.usePdfJs = false
          }
        }
        this.pdfJsTimeout = null
      }, 3000)

      try {
        console.log('[AttachmentsView] Loading PDF with PDF.js:', pdfUrl, 'isMobile:', this.isMobile, 'PDFJS:', typeof PDFJS, 'PDFJS.getDocument:', typeof PDFJS.getDocument)

        if (typeof PDFJS.getDocument !== 'function') {
          throw new Error('PDFJS.getDocument is not a function. PDF.js may not be properly initialized.')
        }

        // Try to load PDF with PDF.js
        // Note: Firebase Storage URLs may have CORS restrictions
        const loadingTask = PDFJS.getDocument({
          url: pdfUrl,
          withCredentials: false,
          httpHeaders: {},
          disableAutoFetch: false,
          disableStream: false,
          // Add CORS mode
          verbosity: 0 // Reduce console noise
        })

        console.log('[AttachmentsView] PDF.js loadingTask created, URL:', pdfUrl)
        console.log('[AttachmentsView] PDF.js loading task created, waiting for promise...')

        // Add progress tracking
        loadingTask.onProgress = (progress) => {
          if (progress.total > 0) {
            const percent = Math.round((progress.loaded / progress.total) * 100)
            console.log('[AttachmentsView] PDF.js loading progress:', percent + '%')
          }
        }

        // Add a race condition with timeout to detect hanging (reduced to 3 seconds for faster feedback)
        const loadPromise = loadingTask.promise
        const timeoutPromise = new Promise((resolve, reject) => {
          setTimeout(() => {
            reject(new Error('PDF loading timeout after 3 seconds. The PDF may be too large, inaccessible, or blocked by CORS restrictions.'))
          }, 3000)
        })

        console.log('[AttachmentsView] Starting PDF.js document load race...')
        try {
          this.pdfJsDocument = await Promise.race([loadPromise, timeoutPromise])
          console.log('[AttachmentsView] PDF.js document load completed')
        } catch (raceError) {
          console.error('[AttachmentsView] PDF.js document load race failed:', raceError)
          throw raceError // Re-throw to be caught by outer try-catch
        }
        // Clear timeout on success
        if (this.pdfJsTimeout) {
          clearTimeout(this.pdfJsTimeout)
          this.pdfJsTimeout = null
        }
        console.log('[AttachmentsView] PDF.js document loaded successfully, pages:', this.pdfJsDocument.numPages)

        // Wait for next tick to ensure canvas ref is available
        await this.$nextTick()
        console.log('[AttachmentsView] After nextTick, checking for canvas ref...')

        // Multiple delays to ensure DOM is ready, especially on mobile
        await new Promise(resolve => setTimeout(resolve, 100))
        await this.$nextTick()
        await new Promise(resolve => setTimeout(resolve, 100))
        await this.$nextTick()

        console.log('[AttachmentsView] Canvas ref available:', !!this.$refs.pdfJsCanvas, 'Container ref:', !!this.$refs.pdfJsContainer)

        // Render first page
        await this.renderPdfPage(1)
        console.log('[AttachmentsView] PDF.js page rendered successfully')
      } catch (error) {
        // Clear timeout on error
        if (this.pdfJsTimeout) {
          clearTimeout(this.pdfJsTimeout)
          this.pdfJsTimeout = null
        }
        console.error('[AttachmentsView] PDF.js error:', error)
        console.error('[AttachmentsView] PDF.js error name:', error.name)
        console.error('[AttachmentsView] PDF.js error message:', error.message)
        console.error('[AttachmentsView] PDF.js error stack:', error.stack)
        if (error.details) {
          console.error('[AttachmentsView] PDF.js error details:', error.details)
        }

        // Provide more specific error messages
        let errorMessage = 'Failed to load PDF. '
        if (error.message && error.message.includes('timeout')) {
          errorMessage += 'The PDF is taking too long to load (likely blocked by CORS restrictions).'
        } else if (error.message && error.message.includes('CORS')) {
          errorMessage += 'Cross-origin restrictions prevent loading this PDF.'
        } else if (error.message && error.message.includes('Invalid PDF')) {
          errorMessage += 'The PDF file appears to be corrupted or invalid.'
        } else if (error.message && error.message.includes('NetworkError')) {
          errorMessage += 'Network error - the PDF cannot be accessed (likely CORS restrictions).'
        } else {
          errorMessage += error.message || 'Unknown error occurred.'
        }
        errorMessage += ' Please try opening it in a new tab.'

        // On mobile, if PDF.js fails, automatically fall back to object/embed instead of showing error
        if (this.isMobile) {
          console.log('[AttachmentsView] PDF.js failed on mobile, falling back to object/embed')
          this.usePdfJs = false
          this.pdfJsLoading = false
          this.pdfJsError = null // Clear error so fallback can show
        } else {
          this.pdfJsError = errorMessage
          this.usePdfJs = false
          this.pdfJsLoading = false
        }
        console.log('[AttachmentsView] PDF.js failed, error message set:', errorMessage)
        console.log('[AttachmentsView] Error state - pdfJsError:', this.pdfJsError, 'pdfJsLoading:', this.pdfJsLoading, 'usePdfJs:', this.usePdfJs)
      }
    },
    async renderPdfPage (pageNumber) {
      console.log('[AttachmentsView] renderPdfPage called, pageNumber:', pageNumber)
      if (!this.pdfJsDocument) {
        console.warn('[AttachmentsView] No PDF document available for rendering')
        return
      }

      // Wait for canvas ref to be available with more retries for mobile
      let retries = 0
      const maxRetries = 20
      while (!this.$refs.pdfJsCanvas && retries < maxRetries) {
        await this.$nextTick()
        await new Promise(resolve => setTimeout(resolve, 100))
        retries++
        if (retries % 5 === 0) {
          console.log('[AttachmentsView] Waiting for canvas ref, retry:', retries)
        }
      }

      if (!this.$refs.pdfJsCanvas) {
        console.error('[AttachmentsView] Canvas ref not available after', maxRetries, 'retries')
        console.log('[AttachmentsView] Available refs:', Object.keys(this.$refs))
        this.pdfJsError = 'Canvas element not found. Please refresh and try again.'
        return
      }

      console.log('[AttachmentsView] Canvas ref found, starting render...')

      try {
        this.pdfJsPage = await this.pdfJsDocument.getPage(pageNumber)
        console.log('[AttachmentsView] PDF page loaded, getting viewport with scale:', this.pdfJsScale)
        const viewport = this.pdfJsPage.getViewport({ scale: this.pdfJsScale })
        console.log('[AttachmentsView] Viewport dimensions:', viewport.width, 'x', viewport.height)

        const canvas = this.$refs.pdfJsCanvas
        if (!canvas) {
          throw new Error('Canvas element not found')
        }

        const context = canvas.getContext('2d')
        if (!context) {
          throw new Error('Could not get canvas context')
        }

        // Set canvas dimensions
        canvas.height = viewport.height
        canvas.width = viewport.width
        console.log('[AttachmentsView] Canvas dimensions set:', canvas.width, 'x', canvas.height)

        // Clear canvas with white background
        context.fillStyle = 'white'
        context.fillRect(0, 0, canvas.width, canvas.height)
        context.clearRect(0, 0, canvas.width, canvas.height)

        const renderContext = {
          canvasContext: context,
          viewport: viewport
        }

        console.log('[AttachmentsView] Starting PDF render...')
        await this.pdfJsPage.render(renderContext).promise
        console.log('[AttachmentsView] PDF.js page rendered successfully:', pageNumber, 'canvas size:', canvas.width, 'x', canvas.height)
        console.log('[AttachmentsView] Canvas visible:', canvas.offsetWidth, 'x', canvas.offsetHeight)
        this.pdfJsCanvas = canvas
        this.pdfJsLoading = false // Ensure loading state is cleared

        // Force Vue to update and ensure canvas is visible
        this.$forceUpdate()
        this.$nextTick(() => {
          // Double-check canvas is visible
          if (canvas && canvas.parentElement) {
            canvas.parentElement.style.display = 'flex'
            canvas.parentElement.style.visibility = 'visible'
            canvas.parentElement.style.opacity = '1'
            canvas.style.display = 'block'
            canvas.style.visibility = 'visible'
            canvas.style.opacity = '1'
            console.log('[AttachmentsView] Canvas visibility forced, parent:', canvas.parentElement.className)
          }
        })
      } catch (error) {
        console.error('[AttachmentsView] PDF.js render error:', error)
        this.pdfJsError = `Failed to render PDF page: ${error.message || 'Unknown error'}`
      }
    },
    async openPdfPreview (item) {
      // Prevent double-triggering if already opening
      if (this.pdfPreviewOpen) {
        return
      }

      console.log('[AttachmentsView] Opening PDF preview for item:', item)
      // Try to get the PDF URL from various possible locations
      // Prefer pdfUrl (stored separately) to avoid using preview image URL
      let pdfUrl = null
      if (item.pdfUrl) {
        pdfUrl = item.pdfUrl
      } else if (item.original && item.original.url) {
        pdfUrl = item.original.url
      } else if (item.originalUrl) {
        pdfUrl = item.originalUrl
      } else if (item.url && item.type === 'application/pdf') {
        // Only use item.url if it's confirmed to be a PDF (not a preview image)
        pdfUrl = item.url
      }

      console.log('[AttachmentsView] PDF URL found:', pdfUrl, 'isSafari:', this.isSafari, 'isMobile:', this.isMobile, 'usePdfJs:', this.usePdfJs)

      if (pdfUrl) {
        // On mobile, try PDF.js first to prevent downloads
        // On desktop, use iframe directly
        this.currentPdfUrl = pdfUrl
        this.pdfPreviewOpen = true

        // Use object/embed for both mobile and desktop to prevent downloads
        // iframe can trigger downloads depending on server headers
        if (this.isMobile) {
          // On mobile, skip PDF.js (CORS issues) and use object/embed directly to prevent downloads
          console.log('[AttachmentsView] Mobile detected - using object/embed to prevent downloads (skipping PDF.js due to CORS)')
          this.usePdfJs = false
          this.pdfJsLoading = false
          this.pdfJsError = null
        } else {
          // Desktop: use object/embed to prevent downloads (more reliable than iframe)
          console.log('[AttachmentsView] Desktop - opening modal with object/embed to prevent downloads')
          this.usePdfJs = false
          this.pdfJsLoading = false
          this.pdfJsError = null
        }

        // Hide header close button after modal opens
        this.$nextTick(() => {
          this.hideHeaderCloseButton()
          // Scroll PDF modal into view
          const modal = document.querySelector('.pdf-modal.popover-modal')
          if (modal) {
            modal.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
          // Ensure scrollable container is properly set up
          this.$nextTick(() => {
            if (this.isMobile) {
              this.ensureMobileScrolling()
            } else {
              this.ensureDesktopScrolling()
            }
          })
        })
      }

      // If we get here, there's no PDF URL (shouldn't happen, but handle gracefully)
      console.warn('[AttachmentsView] No PDF URL found for item:', item)
    },
    hideHeaderCloseButton () {
      // Hide blog close button when PDF is open
      const blogCloseButton = document.querySelector('.blog-close-button')
      if (blogCloseButton) {
        blogCloseButton.style.setProperty('display', 'none', 'important')
        blogCloseButton.style.setProperty('visibility', 'hidden', 'important')
        blogCloseButton.style.setProperty('opacity', '0', 'important')
        blogCloseButton.style.setProperty('pointer-events', 'none', 'important')
      }

      // Inject or update style tag with highest priority CSS
      let styleTag = document.getElementById('pdf-modal-hide-header')
      if (!styleTag) {
        styleTag = document.createElement('style')
        styleTag.id = 'pdf-modal-hide-header'
        document.head.appendChild(styleTag)
      }
      // Update style tag to hide header and blog close button when PDF is open
      styleTag.textContent = `
        .pdf-modal.popover-modal header,
        .pdf-modal.popover-modal .w-full header,
        .pdf-modal.popover-modal > div header,
        .pdf-modal.popover-modal > div > div header,
        .pdf-modal.popover-modal header *,
        .pdf-modal.popover-modal .w-full header *,
        .pdf-modal.popover-modal > div header *,
        .pdf-modal.popover-modal > div > div header *,
        .pdf-modal.popover-modal header button,
        .pdf-modal.popover-modal .w-full header button,
        .pdf-modal.popover-modal > div header button,
        .pdf-modal.popover-modal > div > div header button,
        .pdf-modal.popover-modal header .w-base,
        .pdf-modal.popover-modal header .h-base,
        .pdf-modal.popover-modal header .ml-auto,
        .pdf-modal.popover-modal .w-full header .w-base,
        .pdf-modal.popover-modal .w-full header .h-base,
        .pdf-modal.popover-modal .w-full header .ml-auto {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          height: 0 !important;
          width: 0 !important;
          min-height: 0 !important;
          max-height: 0 !important;
          padding: 0 !important;
          margin: 0 !important;
          pointer-events: none !important;
          position: absolute !important;
          top: -9999px !important;
          left: -9999px !important;
          overflow: hidden !important;
        }
        .pdf-modal.popover-modal ~ .blog-close-button,
        body:has(.pdf-modal.popover-modal) .blog-close-button {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
      `

      // Find and hide the header's close button - use multiple selectors
      const modal = document.querySelector('.pdf-modal.popover-modal')
      if (modal) {
        // Hide entire header - target all possible header locations
        const headers = modal.querySelectorAll('header, .w-full header, .popover-modal header')
        headers.forEach(header => {
          header.style.setProperty('display', 'none', 'important')
          header.style.setProperty('visibility', 'hidden', 'important')
          header.style.setProperty('opacity', '0', 'important')
          header.style.setProperty('height', '0', 'important')
          header.style.setProperty('width', '0', 'important')
          header.style.setProperty('min-height', '0', 'important')
          header.style.setProperty('max-height', '0', 'important')
          header.style.setProperty('padding', '0', 'important')
          header.style.setProperty('margin', '0', 'important')
          header.style.setProperty('pointer-events', 'none', 'important')
          header.style.setProperty('position', 'absolute', 'important')
          header.style.setProperty('top', '-9999px', 'important')
          header.style.setProperty('left', '-9999px', 'important')
        })
        // Hide all buttons in header - be very specific
        const buttonSelectors = [
          'header button',
          '.w-full header button',
          '.popover-modal header button',
          'header .w-base.h-base.ml-auto',
          '.w-full header .w-base.h-base.ml-auto',
          'header .w-base',
          '.w-full header .w-base',
          'button.w-base.h-base.ml-auto'
        ]
        buttonSelectors.forEach(selector => {
          const buttons = modal.querySelectorAll(selector)
          buttons.forEach(button => {
            button.style.setProperty('display', 'none', 'important')
            button.style.setProperty('visibility', 'hidden', 'important')
            button.style.setProperty('opacity', '0', 'important')
            button.style.setProperty('height', '0', 'important')
            button.style.setProperty('width', '0', 'important')
            button.style.setProperty('min-height', '0', 'important')
            button.style.setProperty('max-height', '0', 'important')
            button.style.setProperty('padding', '0', 'important')
            button.style.setProperty('margin', '0', 'important')
            button.style.setProperty('pointer-events', 'none', 'important')
            button.style.setProperty('position', 'absolute', 'important')
            button.style.setProperty('top', '-9999px', 'important')
            button.style.setProperty('left', '-9999px', 'important')
          })
        })
        // Also hide any material-icons in header buttons
        const icons = modal.querySelectorAll('header .material-icons, .w-full header .material-icons')
        icons.forEach(icon => {
          icon.style.setProperty('display', 'none', 'important')
          icon.style.setProperty('visibility', 'hidden', 'important')
          icon.style.setProperty('opacity', '0', 'important')
        })
      }
    },
    getPdfUrlWithoutToolbar (url) {
      // Remove existing hash parameters and add new ones to hide all UI elements
      // Note: Firefox and Chrome often ignore these parameters and show their own toolbars
      // This is a browser security limitation that cannot be overridden from client-side
      const baseUrl = url.split('#')[0]
      // Display with scrollbar enabled, fit to page width, hide all toolbars and panels
      // More aggressive parameters to hide toolbars
      return `${baseUrl}#toolbar=0&navpanes=0&scrollbar=1&zoom=page-width&view=FitH&pagemode=none&hide toolbar=1&hide menubar=1&hide window UI=1&toolbar=0&menubar=0&statusbar=0&messages=0&viewer toolbar=0`
    },
    closePdfPreview () {
      // Clear any pending timeouts first to prevent stuck states
      if (this.pdfJsTimeout) {
        clearTimeout(this.pdfJsTimeout)
        this.pdfJsTimeout = null
      }

      // Clean up blob URL if it was created
      if (this.currentPdfUrl && this.currentPdfUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.currentPdfUrl)
        console.log('[AttachmentsView] Revoked blob URL')
      }

      // Clean up PDF.js resources
      if (this.pdfJsDocument) {
        try {
          this.pdfJsDocument.destroy()
        } catch (e) {
          console.warn('[AttachmentsView] Error destroying PDF.js document:', e)
        }
        this.pdfJsDocument = null
      }

      // Clear all states to prevent stuck loading
      this.pdfJsPage = null
      this.pdfJsCanvas = null
      this.pdfJsError = null
      this.pdfJsLoading = false
      this.usePdfJs = false
      this.pdfPreviewOpen = false
      this.currentPdfUrl = null
      this.pdfError = null
      // Show blog close button again when PDF closes
      this.$nextTick(() => {
        // Update the injected style tag to remove blog-close-button hiding rules
        const styleTag = document.getElementById('pdf-modal-hide-header')
        if (styleTag) {
          // Remove blog-close-button rules from the style tag
          styleTag.textContent = styleTag.textContent.replace(/\.pdf-modal\.popover-modal\s*~\s*\.blog-close-button[^}]*\}/g, '')
          styleTag.textContent = styleTag.textContent.replace(/body:has\(\.pdf-modal\.popover-modal\)\s*\.blog-close-button[^}]*\}/g, '')
        }
        // Directly restore the blog close button with !important to override any remaining styles
        const blogCloseButton = document.querySelector('.blog-close-button')
        if (blogCloseButton) {
          blogCloseButton.style.setProperty('display', 'flex', 'important')
          blogCloseButton.style.setProperty('visibility', 'visible', 'important')
          blogCloseButton.style.setProperty('opacity', '1', 'important')
          blogCloseButton.style.setProperty('pointer-events', 'auto', 'important')
          blogCloseButton.style.setProperty('height', 'auto', 'important')
          blogCloseButton.style.setProperty('width', 'auto', 'important')
          blogCloseButton.style.setProperty('position', 'absolute', 'important')
          blogCloseButton.style.setProperty('top', '0.5rem', 'important')
          blogCloseButton.style.setProperty('right', '0.5rem', 'important')
        }
      })
    },
    scrollPdfUp () {
      const wrapper = this.$refs.pdfWrapper
      if (!wrapper) return

      // On mobile, try parent containers first (PDF might be nested in blog/event modal)
      const isMobile = window.innerWidth <= 480
      if (isMobile) {
        // Method 1: Try PDF modal content box (when nested in blog/event modal)
        const pdfModal = wrapper.closest('.pdf-modal')
        if (pdfModal) {
          const modalContentBox = pdfModal.querySelector('.modal-content-box, .content-box')
          if (modalContentBox) {
            const style = getComputedStyle(modalContentBox)
            if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && modalContentBox.scrollTop > 0) {
              modalContentBox.scrollTop = Math.max(0, modalContentBox.scrollTop - 300)
              return
            }
          }
        }
        // Method 2: Try pdf-preview-container
        const previewContainer = wrapper.closest('.pdf-preview-container')
        if (previewContainer && previewContainer.scrollTop > 0) {
          previewContainer.scrollTop = Math.max(0, previewContainer.scrollTop - 300)
          return
        }
      }

      // Try multiple scroll methods
      // Method 1: Scroll the wrapper directly
      if (wrapper.scrollTop > 0) {
        wrapper.scrollTop = Math.max(0, wrapper.scrollTop - 300)
        return
      }

      // Method 2: Try PDF modal content box (when nested in event modal)
      const pdfModal = wrapper.closest('.pdf-modal')
      if (pdfModal) {
        const modalContentBox = pdfModal.querySelector('.modal-content-box, .content-box')
        if (modalContentBox) {
          const style = getComputedStyle(modalContentBox)
          if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && modalContentBox.scrollTop > 0) {
            modalContentBox.scrollTop = Math.max(0, modalContentBox.scrollTop - 300)
            return
          }
        }
      }

      // Method 3: Try scrollBy
      wrapper.scrollBy({ top: -300, behavior: 'smooth' })

      // Method 4: Try parent containers
      let parent = wrapper.parentElement
      while (parent && parent !== document.body) {
        const style = getComputedStyle(parent)
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          if (parent.scrollTop > 0) {
            parent.scrollTop = Math.max(0, parent.scrollTop - 300)
            return
          }
        }
        parent = parent.parentElement
      }

      // Method 5: Try PDF embed document (may fail due to CORS)
      try {
        const pdfEmbed = wrapper.querySelector('object.pdf-embed, embed.pdf-embed')
        if (pdfEmbed && pdfEmbed.contentDocument && pdfEmbed.contentDocument.body) {
          const pdfBody = pdfEmbed.contentDocument.body
          pdfBody.scrollTop = Math.max(0, pdfBody.scrollTop - 300)
        }
      } catch (e) {
        // Cross-origin restriction
      }
    },
    scrollPdfDown () {
      const wrapper = this.$refs.pdfWrapper
      if (!wrapper) return

      // On mobile, try parent containers first (PDF might be nested in blog/event modal)
      const isMobile = window.innerWidth <= 480
      if (isMobile) {
        // Method 1: Try PDF modal content box (when nested in blog/event modal)
        const pdfModal = wrapper.closest('.pdf-modal')
        if (pdfModal) {
          const modalContentBox = pdfModal.querySelector('.modal-content-box, .content-box')
          if (modalContentBox) {
            const style = getComputedStyle(modalContentBox)
            if ((style.overflowY === 'auto' || style.overflowY === 'scroll') &&
                modalContentBox.scrollTop < modalContentBox.scrollHeight - modalContentBox.clientHeight) {
              modalContentBox.scrollTop = Math.min(modalContentBox.scrollHeight - modalContentBox.clientHeight, modalContentBox.scrollTop + 300)
              return
            }
          }
        }
        // Method 2: Try pdf-preview-container
        const previewContainer = wrapper.closest('.pdf-preview-container')
        if (previewContainer && previewContainer.scrollTop < previewContainer.scrollHeight - previewContainer.clientHeight) {
          previewContainer.scrollTop = Math.min(previewContainer.scrollHeight - previewContainer.clientHeight, previewContainer.scrollTop + 300)
          return
        }
      }

      // Try multiple scroll methods
      // Method 1: Scroll the wrapper directly
      if (wrapper.scrollTop < wrapper.scrollHeight - wrapper.clientHeight) {
        wrapper.scrollTop = Math.min(wrapper.scrollHeight - wrapper.clientHeight, wrapper.scrollTop + 300)
        return
      }

      // Method 2: Try PDF modal content box (when nested in event modal)
      const pdfModal = wrapper.closest('.pdf-modal')
      if (pdfModal) {
        const modalContentBox = pdfModal.querySelector('.modal-content-box, .content-box')
        if (modalContentBox) {
          const style = getComputedStyle(modalContentBox)
          if ((style.overflowY === 'auto' || style.overflowY === 'scroll') &&
              modalContentBox.scrollTop < modalContentBox.scrollHeight - modalContentBox.clientHeight) {
            modalContentBox.scrollTop = Math.min(modalContentBox.scrollHeight - modalContentBox.clientHeight, modalContentBox.scrollTop + 300)
            return
          }
        }
      }

      // Method 3: Try scrollBy
      wrapper.scrollBy({ top: 300, behavior: 'smooth' })

      // Method 4: Try parent containers
      let parent = wrapper.parentElement
      while (parent && parent !== document.body) {
        const style = getComputedStyle(parent)
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
          if (parent.scrollTop < parent.scrollHeight - parent.clientHeight) {
            parent.scrollTop = Math.min(parent.scrollHeight - parent.clientHeight, parent.scrollTop + 300)
            return
          }
        }
        parent = parent.parentElement
      }

      // Method 5: Try PDF embed document (may fail due to CORS)
      try {
        const pdfEmbed = wrapper.querySelector('object.pdf-embed, embed.pdf-embed')
        if (pdfEmbed && pdfEmbed.contentDocument && pdfEmbed.contentDocument.body) {
          const pdfBody = pdfEmbed.contentDocument.body
          pdfBody.scrollTop = Math.min(pdfBody.scrollHeight - pdfBody.clientHeight, pdfBody.scrollTop + 300)
        }
      } catch (e) {
        // Cross-origin restriction
      }
    },
    ensureMobileScrolling () {
      // Ensure mobile PDF scrolling works - use pdf-wrapper as scrollable container
      const wrapper = this.$refs.pdfWrapper
      if (wrapper && this.isMobile) {
        // Make wrapper the scrollable container
        wrapper.setAttribute('style', 'overflow-y: auto !important; overflow-x: hidden !important; -webkit-overflow-scrolling: touch !important; touch-action: pan-y !important; pointer-events: auto !important; height: 100vh !important; max-height: 100vh !important; position: relative !important;')

        // Ensure PDF is visible and can render
        const pdfElements = wrapper.querySelectorAll('object, embed')
        pdfElements.forEach((pdfElement) => {
          pdfElement.style.pointerEvents = 'auto'
          pdfElement.style.touchAction = 'pan-y'
          pdfElement.style.visibility = 'visible'
          pdfElement.style.opacity = '1'
          pdfElement.style.display = 'block'
          pdfElement.style.width = '100%'
          pdfElement.style.height = '100vh'
          pdfElement.style.minHeight = '100vh'
        })

        // Ensure content can expand
        this.$nextTick(() => {
          const container = wrapper.querySelector('.pdf-mobile-container')
          const contentWrapper = wrapper.querySelector('.pdf-content-wrapper')
          const pdfEmbed = wrapper.querySelector('object, embed')

          // Make all wrappers allow expansion
          if (contentWrapper) {
            contentWrapper.style.height = 'auto'
            contentWrapper.style.minHeight = 'auto'
            contentWrapper.style.maxHeight = 'none'
            contentWrapper.style.overflow = 'visible'
          }
          if (container) {
            container.style.height = 'auto'
            container.style.minHeight = 'auto'
            container.style.maxHeight = 'none'
            container.style.overflow = 'visible'
            container.style.pointerEvents = 'auto'
          }

          // Wait for PDF to load and then ensure it's scrollable
          setTimeout(() => {
            if (pdfEmbed) {
              // Get actual PDF dimensions
              const pdfHeight = pdfEmbed.offsetHeight || pdfEmbed.scrollHeight || 0
              const viewportHeight = window.innerHeight

              // Only set min-height if PDF is actually taller than viewport
              if (pdfHeight > 0 && pdfHeight > viewportHeight) {
                // PDF content is taller - ensure container can scroll it
                pdfEmbed.style.height = 'auto'
                pdfEmbed.style.minHeight = pdfHeight + 'px'
              } else if (pdfHeight === 0) {
                // PDF hasn't loaded yet - set a reasonable default
                pdfEmbed.style.height = 'auto'
                pdfEmbed.style.minHeight = Math.max(viewportHeight * 1.5, 1200) + 'px'
              } else {
                // PDF fits in viewport - let it be its natural size
                pdfEmbed.style.height = 'auto'
                pdfEmbed.style.minHeight = 'auto'
              }
            }

            // Force wrapper to recalculate
            wrapper.style.height = '100vh'
            wrapper.style.maxHeight = '100vh'

            // Force a reflow
            setTimeout(() => {
              wrapper.scrollTop = 0
              void wrapper.offsetHeight
              // Log for debugging
              console.log('[AttachmentsView] Mobile scrolling setup:', {
                wrapperHeight: wrapper.clientHeight,
                wrapperScrollHeight: wrapper.scrollHeight,
                canScroll: wrapper.scrollHeight > wrapper.clientHeight,
                pdfHeight: pdfEmbed ? pdfEmbed.offsetHeight : 'not found',
                pdfScrollHeight: pdfEmbed ? pdfEmbed.scrollHeight : 'not found',
                viewportHeight: window.innerHeight,
                contentWrapperHeight: contentWrapper ? contentWrapper.offsetHeight : 'not found',
                containerHeight: container ? container.offsetHeight : 'not found'
              })
            }, 100)
          }, 1000)
        })
      }
    },
    ensureDesktopScrolling () {
      // Ensure desktop PDF scrolling works
      const wrapper = this.$refs.pdfWrapper
      if (wrapper && !this.isMobile) {
        // Ensure wrapper is scrollable
        wrapper.style.overflowY = 'auto'
        wrapper.style.overflowX = 'hidden'
        wrapper.style.webkitOverflowScrolling = 'touch'

        // Ensure content can expand
        const contentWrapper = wrapper.querySelector('.pdf-content-wrapper')
        if (contentWrapper) {
          contentWrapper.style.height = 'auto'
          contentWrapper.style.minHeight = '100vh'
          contentWrapper.style.overflow = 'visible'
        }

        const iframeContainer = wrapper.querySelector('.pdf-iframe-container')
        if (iframeContainer) {
          iframeContainer.style.height = 'auto'
          iframeContainer.style.minHeight = '100vh'
          iframeContainer.style.overflow = 'visible'
        }

        // Ensure PDF embed can expand
        const pdfEmbed = wrapper.querySelector('object, embed')
        if (pdfEmbed) {
          pdfEmbed.style.height = 'auto'
          pdfEmbed.style.minHeight = '100vh'
        }

        // Force a reflow
        this.$nextTick(() => {
          wrapper.scrollTop = 0
          wrapper.scrollTop = 1
          wrapper.scrollTop = 0
          console.log('[AttachmentsView] Desktop scrolling setup:', {
            wrapperHeight: wrapper.clientHeight,
            contentHeight: wrapper.scrollHeight,
            canScroll: wrapper.scrollHeight > wrapper.clientHeight
          })
        })
      }
    }
  }
}
</script>

<style lang="scss">
  .attachments-view {
    .outline {
      border: 1px solid #f2f2f2;
    }

    // PDF thumbnail wrapper styling
    .pdf-thumbnail-wrapper {
      display: block;
      width: auto;
      height: auto;
      min-height: 0;
      max-width: 100%;

      img {
        display: block;
        max-width: 100%;
        height: auto;
      }

      .pdf-placeholder {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 150px;
        width: 100%;
      }
    }

    // Prevent PDF images from being treated as downloadable links
    .attachment-box img[class*="cursor-pointer"] {
      pointer-events: auto !important;
      user-select: none !important;
      -webkit-user-select: none !important;
      -moz-user-select: none !important;
      -ms-user-select: none !important;
      // Prevent drag and drop download
      -webkit-user-drag: none !important;
      -khtml-user-drag: none !important;
      -moz-user-drag: none !important;
      -o-user-drag: none !important;
      user-drag: none !important;
    }

    .pdf-view-button {
      font-size: 1.25rem !important;

      span {
        font-size: 1.25rem !important;
        font-weight: 500;
      }
      @media (max-width: 30rem) {
        padding: 0.5rem 0.75rem;
        font-size: 1rem !important;
        min-width: auto;

        span {
          font-size: 1rem !important;
        }
      }
    }
    video {
      width: 100%;
      max-width: 100%;
      height: auto;
      display: block;
    }
    .attachment {
      border: none !important;
      border-top: none !important;
      box-shadow: none !important;
      &::before,
      &::after {
        display: none !important;
        border: none !important;
      }
    }
    .attachment-box {
      border: none !important;
      border-top: none !important;
      box-shadow: none !important;
      &::before,
      &::after {
        display: none !important;
        border: none !important;
      }
      img {
        border: none !important;
        border-top: none !important;
        outline: none !important;
        box-shadow: none !important;
        display: block;
        &::before,
        &::after {
          display: none !important;
          border: none !important;
        }
      }
    }
  }
  .pdf-modal.popover-modal {
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    width: 90vw !important;
    height: 90vh !important;
    max-width: 90vw !important;
    max-height: 90vh !important;
    margin: 0 !important;
    padding: 0 !important;
    position: fixed !important;
    z-index: 999999 !important;
    background: transparent !important;
    opacity: 1 !important;
    // Create new stacking context to ensure PDF modal is above everything
    isolation: isolate !important;
    // Ensure it doesn't affect parent popup sizing
    box-sizing: border-box !important;

    // Hide modal shadow to prevent black flash
    .modal-shadow {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      background: transparent !important;
    }

    @media (max-width: 30rem) {
      width: 100vw !important;
      max-width: 100vw !important;
      height: 100vh !important;
      max-height: 100vh !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      transform: none !important;
      z-index: 999999 !important;
      position: fixed !important;
      // Create new stacking context to ensure PDF modal is above event modal
      isolation: isolate !important;
    }

    // Hide blog close button when PDF modal is open
    ~ .blog-close-button,
    + .blog-close-button {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }

    // Hide header and all its contents
    .w-full header,
    .w-full > header,
    > div header,
    > div > div header {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      height: 0 !important;
      width: 0 !important;
      pointer-events: none !important;
      position: absolute !important;
      top: -9999px !important;
      left: -9999px !important;
    }

    // Hide all buttons in header
    .w-full header button,
    .w-full header .w-base,
    .w-full header .h-base,
    > div header button,
    > div header .w-base,
    > div header .h-base,
    > div > div header button,
    > div > div header .w-base,
    > div > div header .h-base {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      height: 0 !important;
      width: 0 !important;
      pointer-events: none !important;
      position: absolute !important;
      top: -9999px !important;
      left: -9999px !important;
    }

    @media (max-width: 30rem) {
      width: 100vw !important;
      height: 100vh !important;
      max-width: 100vw !important;
      max-height: 100vh !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      transform: none !important;
      border-radius: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      background: white !important;
      z-index: 10001 !important;
      position: fixed !important;
    }

    > div {
      width: 100% !important;
      height: 100% !important;
      max-width: 100% !important;
      max-height: 100% !important;
      display: flex !important;
      flex-direction: column !important;
      overflow: hidden !important;
      margin: 0 !important;
      padding: 0 !important;
      box-sizing: border-box !important;
      position: relative !important;
      background: white !important;
      top: 0 !important;
      left: 0 !important;
      transition: none !important;

      @media (max-width: 30rem) {
        width: 100vw !important;
        height: 100vh !important;
        max-width: 100vw !important;
        max-height: 100vh !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        background: white !important;
        position: absolute !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        transition: none !important;
        overflow: visible !important;
        // Allow child to scroll
      }
    }

    .w-full {
      max-width: 90vw !important;
      max-height: 90vh !important;
      width: 90vw !important;
      height: 90vh !important;
      display: flex !important;
      flex-direction: column !important;
      overflow: hidden !important;
      border-radius: 4px;
      position: relative !important;
      margin: 0 !important;
      padding: 0 !important;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      box-sizing: border-box !important;
      background: white !important;

      @media (max-width: 30rem) {
        max-width: 100vw !important;
        max-height: 100vh !important;
        width: 100vw !important;
        height: 100vh !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        overflow: hidden !important;
        background: white !important;
      }

      header {
        display: none !important;
        height: 0 !important;
        min-height: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
        overflow: hidden !important;
        visibility: hidden !important;
      }
    }

    // Hide header directly on PDF modal
    header,
    .w-full header,
    > div header,
    > div > div header {
      display: none !important;
      height: 0 !important;
      min-height: 0 !important;
      padding: 0 !important;
      margin: 0 !important;
      overflow: hidden !important;
      visibility: hidden !important;
      opacity: 0 !important;
      position: absolute !important;
      top: -9999px !important;
      left: -9999px !important;
      width: 0 !important;
      max-width: 0 !important;
      pointer-events: none !important;
    }

    // Hide all buttons inside header for PDF modal
    header *,
    .w-full header *,
    > div header *,
    > div > div header * {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
    .modal-shadow {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
      background: transparent !important;
      height: 0 !important;
      width: 0 !important;
      z-index: -1 !important;

      @media (max-width: 30rem) {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        background: transparent !important;
        height: 0 !important;
        width: 0 !important;
        z-index: -1 !important;
    }
    }
  }

  // Disable fade transition for PDF modal to prevent flash
  // Target the transition wrapper and child elements
  .pdf-modal.fade-enter-active,
  .pdf-modal.fade-leave-active {
    transition: none !important;
  }

  .pdf-modal.fade-enter,
  .pdf-modal.fade-leave-to {
    opacity: 1 !important;
  }

  // Ensure white background on all transition states and hide shadow
  .pdf-modal.fade-enter-active .popover-modal,
  .pdf-modal.fade-leave-active .popover-modal,
  .pdf-modal.fade-enter .popover-modal,
  .pdf-modal.fade-leave-to .popover-modal,
  .pdf-modal.fade-enter-active .popover-modal > div,
  .pdf-modal.fade-leave-active .popover-modal > div,
  .pdf-modal.fade-enter .popover-modal > div,
  .pdf-modal.fade-leave-to .popover-modal > div {
    background: white !important;
    opacity: 1 !important;
  }

  // Hide modal shadow for PDF modal to prevent black flash
  .pdf-modal .modal-shadow {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    background: transparent !important;
  }
  .pdf-close-button {
    position: fixed;
    top: 1rem;
    right: 1rem;
    z-index: 10011 !important;
    width: auto;
    height: auto;
    min-width: auto;
    min-height: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent !important;
    border: none !important;
    border-radius: 0 !important;
    cursor: pointer;
    box-shadow: none !important;
    padding: 0;
    margin: 0;
    transition: opacity 0.2s;

    @media (max-width: 30rem) {
      top: 0.5rem;
      right: 0.5rem;
    }

    &:hover {
      opacity: 0.7;
    }
    i {
      font-size: 1.5rem;
      color: #333;

      @media (max-width: 30rem) {
        font-size: 1.2rem;
    }
  }
  }

  .pdf-scroll-button {
    position: absolute;
    z-index: 10002 !important;
    width: auto;
    height: auto;
    min-width: auto;
    min-height: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent !important;
    border: none !important;
    border-radius: 0 !important;
    cursor: pointer;
    box-shadow: none !important;
    padding: 0;
    margin: 0;
    transition: opacity 0.2s;
    left: 50%;
    transform: translateX(-50%);
    max-width: calc(100% - 2rem);
    pointer-events: auto;

    @media (max-width: 30rem) {
      display: none !important;
    }

    &:hover {
      opacity: 0.7;
      transform: translateX(-50%);
    }

    i {
      font-size: 1.5rem;
      color: #333;
    }

    &.pdf-scroll-up {
      top: 1rem;
    }

    &.pdf-scroll-down {
      bottom: 1rem;
    }
  }

  .pdf-preview-container {
    flex: 1;
    width: 100%;
    height: 100%;
    max-height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    top: 0;
    left: 0;
    overflow: hidden;
    padding: 0;
    margin: 0;
    min-height: 0;
    background: white;
    z-index: 10;

    box-sizing: border-box;

    @media (max-width: 30rem) {
      overflow: hidden !important;
      width: 100% !important;
      height: 100vh !important;
      max-width: 100% !important;
      max-height: 100vh !important;
      pointer-events: auto !important;
      box-sizing: border-box !important;
      position: relative !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      display: flex !important;
      flex-direction: column !important;
      padding: 0 !important;
      margin: 0 !important;
      align-items: stretch !important;
      justify-content: flex-start !important;
      z-index: 10 !important;
    }
  }
  .pdf-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    z-index: 10;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    box-sizing: border-box;
    background: white;
    min-height: 0;
    max-height: 100%;
    -webkit-overflow-scrolling: touch;
    padding-top: 0;
    margin-top: 0;
    touch-action: pan-y;
    scroll-behavior: smooth;
    overscroll-behavior: contain;

    // Ensure wrapper can scroll when content is taller
    &.pdfjs-viewer {
      .pdf-content-wrapper {
        overflow-y: auto;
        overflow-x: hidden;
      }
    }

    @media (max-width: 30rem) {
      position: relative !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      width: 100% !important;
      height: 100vh !important;
      min-height: 100vh !important;
      max-width: 100% !important;
      max-height: 100vh !important;
      overflow-y: auto !important;
      overflow-x: hidden !important;
      -webkit-overflow-scrolling: touch !important;
      touch-action: pan-y !important;
      box-sizing: border-box !important;
      flex: 1 1 auto !important;
      padding-top: 0 !important;
      margin-top: 0 !important;
      padding-bottom: 0 !important;
      margin-bottom: 0 !important;
    }
  }

  .pdf-content-wrapper {
    position: relative;
    width: 100%;
    height: auto;
    min-height: 100%;
    flex: 1 1 auto;
    overflow: visible !important;
    box-sizing: border-box;
    padding-top: 0;
    margin-top: 0;
    display: flex;
    flex-direction: column;
    background: white !important;

    @media (max-width: 30rem) {
      min-height: auto !important;
      height: auto !important;
    }

    @media (max-width: 30rem) {
      position: relative !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      width: 100% !important;
      height: auto !important;
      min-height: auto !important;
      max-width: 100% !important;
      max-height: none !important;
      box-sizing: border-box !important;
      overflow: visible !important;
      padding-top: 0 !important;
      margin-top: 0 !important;
      padding-bottom: 0 !important;
      margin-bottom: 0 !important;
      display: flex !important;
      pointer-events: auto !important;
      flex-direction: column !important;
      background: white !important;
    }
  }

  .pdf-toolbar-overlay {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    height: 40px !important;
    background: rgba(208, 208, 208, 0.9) !important;
    z-index: 10000 !important;
    pointer-events: none !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    box-sizing: border-box !important;
    visibility: visible !important;
    opacity: 1 !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;

    @media (max-width: 30rem) {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      width: 100% !important;
      height: 30px !important;
      background: rgba(208, 208, 208, 0.9) !important;
      z-index: 10000 !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      visibility: visible !important;
      opacity: 1 !important;
      margin: 0 !important;
      padding: 0 !important;
      pointer-events: none !important;
      box-sizing: border-box !important;
    }
  }
  .reading-view-label {
    color: #333 !important;
    font-size: 0.875rem !important;
    font-weight: 500 !important;
    pointer-events: none !important;
    text-align: center !important;
    visibility: visible !important;
    opacity: 1 !important;
    display: block !important;

    @media (max-width: 30rem) {
      font-size: 0.75rem !important;
      visibility: visible !important;
      opacity: 1 !important;
      display: block !important;
    }
  }

  // Hide any browser PDF viewer toolbars and overlays
  // BUT NOT our custom toolbar overlay
  .pdf-wrapper {
    // Target browser-specific PDF viewer elements, but exclude our custom overlay
    object *,
    embed * {
      &[class*="toolbar"]:not(.pdf-toolbar-overlay),
      &[class*="Toolbar"]:not(.pdf-toolbar-overlay),
      &[id*="toolbar"],
      &[id*="Toolbar"],
      &[class*="reader"]:not(.reading-view-label),
      &[class*="Reader"]:not(.reading-view-label),
      &[id*="reader"],
      &[id*="Reader"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        height: 0 !important;
        pointer-events: none !important;
      }
    }

    @media (max-width: 30rem) {
      // More aggressive hiding on mobile - but be careful not to hide PDF content
      object *,
      embed * {
        &[class*="toolbar"]:not(.pdf-toolbar-overlay):not([class*="content"]):not([class*="page"]):not([class*="canvas"]),
        &[class*="Toolbar"]:not(.pdf-toolbar-overlay):not([class*="content"]):not([class*="page"]):not([class*="canvas"]),
        &[id*="toolbar"]:not([id*="content"]):not([id*="page"]):not([id*="canvas"]),
        &[id*="Toolbar"]:not([id*="content"]):not([id*="page"]):not([id*="canvas"]),
        &[class*="reader"]:not(.reading-view-label):not([class*="content"]):not([class*="page"]),
        &[class*="Reader"]:not(.reading-view-label):not([class*="content"]):not([class*="page"]),
        &[id*="reader"]:not([id*="content"]):not([id*="page"]),
        &[id*="Reader"]:not([id*="content"]):not([id*="page"]) {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          height: 0 !important;
          width: 0 !important;
          pointer-events: none !important;
          position: absolute !important;
          top: -9999px !important;
          left: -9999px !important;
        }
      }
    }
  }
  .pdf-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 1.2rem;
    color: #666;

    @media (max-width: 30rem) {
      font-size: 1rem;
      padding: 1rem;
    }
  }
  .pdf-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 2rem;

    @media (max-width: 30rem) {
      padding: 1rem;
      font-size: 0.875rem;
    }
  }
  .pdf-embed {
    width: 100%;
    height: 100%;
    min-height: 100%;
    border: none;
    flex: 1 1 auto;
    display: block;
    margin: 0;
    padding: 0;
    background: white !important;
    visibility: visible !important;
    opacity: 1 !important;
    z-index: 1;
    box-sizing: border-box;
    position: relative;

    @media (max-width: 30rem) {
      position: relative !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      width: 100% !important;
      height: 100% !important;
      min-height: 100% !important;
      max-width: 100% !important;
      max-height: 100% !important;
      flex: 1 1 auto !important;
      box-sizing: border-box !important;
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
      overflow: visible !important;
      background: white !important;
  }

    // Hide browser PDF viewer toolbars
    &::-webkit-scrollbar {
      width: 8px;
    }
  }

  object.pdf-embed,
  embed.pdf-embed {
    background: white !important;
    border: none;
    outline: none;
    box-shadow: none;
    visibility: visible !important;
    opacity: 1 !important;
    display: block !important;
    box-sizing: border-box;
    width: 100% !important;
    height: auto !important;
    min-height: 100% !important;

    @media (max-width: 30rem) {
      position: relative !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: auto !important;
      width: 100% !important;
      height: auto !important;
      min-height: 100vh !important;
      max-width: 100% !important;
      max-height: none !important;
      box-sizing: border-box !important;
      border: none !important;
      margin: 0 !important;
      padding: 0 !important;
      visibility: visible !important;
      opacity: 1 !important;
      display: block !important;
      overflow: visible !important;
      background: white !important;
      // Allow PDF to expand beyond viewport for scrolling
      position: relative !important;
    }

    &.pdf-mobile-embed {
      @media (max-width: 30rem) {
        pointer-events: auto !important;
        touch-action: pan-y !important;
        width: 100% !important;
        height: 100vh !important;
        min-height: 100vh !important;
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        position: relative !important;
        background: white !important;
        z-index: 1 !important;
        // Allow PDF to render with proper dimensions
      }
    }

    &.pdf-desktop-embed {
      pointer-events: auto !important;
      width: 100% !important;
      height: auto !important;
      min-height: 100vh !important;
      display: block !important;
      border: none !important;
      background: white !important;
      // Allow PDF to expand beyond viewport for scrolling
    }
  }

  // Safari-specific PDF styles
  .pdf-iframe-container {
    width: 100% !important;
    height: auto !important;
    min-height: 100vh !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: visible !important;
    position: relative !important;

    @media (max-width: 30rem) {
      width: 100% !important;
      height: 100vh !important;
      min-height: 100vh !important;
      max-height: none !important;
      overflow: visible !important;
      position: relative !important;
      pointer-events: auto !important;
      // Ensure container has proper dimensions for PDF to render
    }

    &.pdf-mobile-container {
      @media (max-width: 30rem) {
        height: 100vh !important;
        min-height: 100vh !important;
        max-height: none !important;
        overflow: visible !important;
        // Let parent .pdf-wrapper handle scrolling
        display: block !important;
        position: relative !important;
        width: 100% !important;
        pointer-events: auto !important;
        z-index: 1 !important;
        background: white !important;
        // Ensure container has proper dimensions for PDF to render
      }
    }
  }

  .pdf-embed-safari,
  iframe.pdf-embed {
    width: 100% !important;
    height: 100% !important;
    min-height: 100vh !important;
    max-width: 100% !important;
    max-height: 100% !important;
    border: none !important;
    margin: 0 !important;
    padding: 0 !important;
    background: white !important;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    box-sizing: border-box !important;
    position: relative !important;
    flex: 1 1 auto !important;

    @media (max-width: 30rem) {
      position: relative !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      width: 100% !important;
      height: 100% !important;
      min-height: 100% !important;
      max-width: 100% !important;
      max-height: 100% !important;
      box-sizing: border-box !important;
      border: none !important;
      margin: 0 !important;
      padding: 0 !important;
      visibility: visible !important;
      opacity: 1 !important;
      display: block !important;
      overflow: visible !important;
      background: white !important;
    }
  }

  // PDF.js canvas styles
  .pdfjs-canvas-container {
    width: 100% !important;
    height: 100% !important;
    min-height: 100% !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: flex-start !important;
    padding: 1rem !important;
    box-sizing: border-box !important;
    position: relative !important;
    visibility: visible !important;
    opacity: 1 !important;
    background: white !important;

    @media (max-width: 30rem) {
      padding: 0.5rem !important;
      min-height: calc(100vh - 4rem) !important;
    }

    .pdfjs-canvas {
      max-width: 100% !important;
      width: auto !important;
      height: auto !important;
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
      background: white !important;
      margin: 0 auto !important;
      position: relative !important;
    }
  }

  .pdf-wrapper.pdfjs-viewer {
    .pdf-content-wrapper {
      overflow-y: auto;
      overflow-x: hidden;
    }
  }

  // Safari PDF wrapper adjustments
  .pdf-wrapper.safari-pdf {
    // Safari may need different overflow handling
    overflow-y: auto !important;
    overflow-x: hidden !important;
    -webkit-overflow-scrolling: touch !important;

    .pdf-content-wrapper {
      // Ensure Safari PDF iframe fills container
      display: flex !important;
      flex-direction: column !important;
      height: 100% !important;
      min-height: 100% !important;
    }
  }
  /* Try to hide PDF viewer toolbar - may not work due to cross-origin restrictions */
  .pdf-embed::-webkit-scrollbar {
    width: 8px;
  }
  .pdf-embed::-webkit-scrollbar-track {
    background: transparent;
  }
  .pdf-embed::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
  /* Attempt to hide PDF viewer UI elements - limited by cross-origin restrictions */
  object.pdf-embed,
  embed.pdf-embed {
    overflow: hidden;

    @media (max-width: 30rem) {
      overflow: visible !important;
    }
  }

  /* More aggressive hiding of browser PDF toolbars */
  .pdf-wrapper object,
  .pdf-wrapper embed {
    /* Hide any toolbar elements that browsers inject */
    & > *[class*="toolbar"],
    & > *[class*="Toolbar"],
    & > *[id*="toolbar"],
    & > *[id*="Toolbar"],
    & > *[class*="chrome"],
    & > *[class*="Chrome"],
    & > *[class*="firefox"],
    & > *[class*="Firefox"] {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      height: 0 !important;
      pointer-events: none !important;
    }

    @media (max-width: 30rem) {
      /* Even more aggressive on mobile - but exclude PDF content elements */
      & > *[class*="toolbar"]:not([class*="content"]):not([class*="page"]):not([class*="canvas"]),
      & > *[class*="Toolbar"]:not([class*="content"]):not([class*="page"]):not([class*="canvas"]),
      & > *[id*="toolbar"]:not([id*="content"]):not([id*="page"]):not([id*="canvas"]),
      & > *[id*="Toolbar"]:not([id*="content"]):not([id*="page"]):not([id*="canvas"]) {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        height: 0 !important;
        width: 0 !important;
        pointer-events: none !important;
        position: absolute !important;
        top: -9999px !important;
        left: -9999px !important;
      }
    }
  }
</style>
